/**
 * The words under the chart, as a pure function.
 *
 * Kept out of the component because the interesting part is the branching,
 * not the markup: a goal can be achieved, overdue, open-ended, inside its
 * last week, or simply under way, and each of those has to say something
 * different and true. A sentence built inside JSX can only be checked by
 * signing in and looking at it in exactly the right state.
 */
import { computeGoalPace, type Goal } from '@loggy/shared';

export type GoalStatus = 'achieved' | 'overdue' | 'on-track';

export interface GoalReadout {
  status: GoalStatus;
  /** The heading: names the goal and its target. */
  heading: string;
  /** The sentence: what is left, and what it costs per week. */
  detail: string;
}

export interface GoalReadoutInput {
  nodeTitle: string;
  unit: string;
  precision: number;
  goal: Pick<Goal, 'targetValue' | 'baselineValue' | 'targetDate' | 'direction'> & {
    title: string;
    achieved: boolean;
    currentValue: number | null;
  };
}

/** Short, and never ambiguous about the year — "9 Dec 2026". `en-GB` for
 *  day-first ordering, matching every formatter in the chart this sits under;
 *  plain `en` would render "Dec 9, 2026" against the chart's own "9 Dec". */
const deadlineFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

export function formatDeadline(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return deadlineFormatter.format(date);
}

export function buildGoalReadout(input: GoalReadoutInput, now: Date = new Date()): GoalReadout {
  const { nodeTitle, unit, precision, goal } = input;

  const pace = computeGoalPace(
    {
      targetValue: goal.targetValue,
      baselineValue: goal.baselineValue,
      targetDate: goal.targetDate,
      direction: goal.direction,
    },
    goal.currentValue,
    now,
  );

  const amount = (value: number) => `${value.toFixed(precision)} ${unit}`;
  const target = amount(goal.targetValue);

  const status: GoalStatus = goal.achieved
    ? 'achieved'
    : pace.daysRemaining !== null && pace.daysRemaining < 0
      ? 'overdue'
      : 'on-track';

  // The goal's own title is usually just the node's name repeated; saying it
  // twice reads as a stutter, so it only earns its place when it differs.
  const heading = goal.title === nodeTitle ? `Goal ${target}` : `${goal.title} — ${target}`;

  return { status, heading, detail: buildDetail() };

  function buildDetail(): string {
    if (status === 'achieved') return `Reached — you are at or past ${target}.`;
    if (pace.remaining === null) {
      return 'No readings yet, so there is nothing to measure the distance from.';
    }

    const toGo = `${amount(pace.remaining)} to go`;

    if (status === 'overdue') {
      // `targetDate` is non-null here by construction: `daysRemaining` is only
      // a number when there is a deadline to count down to.
      return `${toGo}. The ${formatDeadline(goal.targetDate!)} deadline has passed.`;
    }
    if (goal.targetDate === null) return `${toGo}, with no deadline set.`;

    if (pace.perWeek === null) {
      // Inside the last week. `computeGoalPace` withholds a rate here on
      // purpose — "14 kg a week" is arithmetically true and useless — so the
      // date and the days left carry it instead.
      const days = pace.daysRemaining ?? 0;
      const left = days === 1 ? '1 day' : `${days} days`;
      return `${toGo} by ${formatDeadline(goal.targetDate)} — ${left} left.`;
    }

    const verb = pace.direction === 'lose' ? 'Lose' : 'Gain';
    return `${toGo}. ${verb} ${amount(pace.perWeek)} a week to arrive by ${formatDeadline(goal.targetDate)}.`;
  }
}
